import type { BoxProps, MotionPropsFor } from '@hover/blueprint';
import { Box, Flex, Grid, motion, useTheme } from '@hover/blueprint';
import type { AnimationControls } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import type { BottomTextProps } from 'src/modules/homeownerApp/components/BottomText';
import {
  Selector,
  SelectorItem,
} from 'src/shared/components/Selector/Selector';
import { useRandom } from 'src/shared/hooks';
import type { ButtonPressed } from 'src/shared/telemetry';
import { buttonPressed } from 'src/shared/telemetry';
import { hideScrollbars } from 'src/shared/utils/styles';

import { useProTranslate } from '../../hooks';

import { SampleImage } from './SampleImage';
import type { LandingPageSpaceType, LandingPageStyle } from './SampleImage';
import { Switch } from './Switch';
import { TryItCard } from './TryItCard';
import type { SampleImageMeta } from './utils';
import { getSampleImages } from './utils';

const MotionHiddenScrollBox = motion(hideScrollbars(Box));

const smallerTextProps: BottomTextProps = {
  primaryProps: { size: 200 },
  secondaryProps: { size: 200 },
};

interface StyleSelectorProps extends MotionPropsFor<BoxProps> {
  selectedStyle?: LandingPageStyle;
  setStyle: (style?: LandingPageStyle) => void;
  setIsBeforeSelected: (isBeforeSelected: boolean) => void;
  headerOffsetControls?: AnimationControls;
  isSticky?: boolean;
  trackingData?: Partial<ButtonPressed>;
  styles?: LandingPageStyle[];
}

export const StyleSelector = ({
  selectedStyle,
  setStyle,
  setIsBeforeSelected,
  headerOffsetControls,
  isSticky,
  trackingData,
  styles,
  ...rest
}: StyleSelectorProps) => {
  const t = useProTranslate();

  const onStyleChange = (newStyle: LandingPageStyle | 'All Styles') => {
    buttonPressed({
      ...trackingData,
      button_text: newStyle,
      button_location: 'top',
      primary_cta: false,
      track_id: 'style-filter',
    });
    setStyle(newStyle === 'All Styles' ? undefined : newStyle);
    setIsBeforeSelected(false);
  };
  const { zIndices } = useTheme();

  // This negative margin and the corresponding positive margin in the parent is a hacky way of making the bounding box for
  // the before/after toggle operate midway through the image elements. Design wanted this behavior so that the sticky
  // button did not show until further down the page. Another negative/position margin pair exists at the end of the parent
  // element in order to lock the bottom bound of the button to about 75% of the way through the last image.
  const marginTop = { base: '-300px', tablet: '0px !important' };

  return (
    <MotionHiddenScrollBox
      alignItems="center"
      animate={headerOffsetControls}
      height="100%"
      justifyContent={{
        tablet: 'center',
        base: 'left',
      }}
      marginTop={marginTop}
      overflowX="scroll"
      pb={{ base: 500, tablet: 700 }}
      position={isSticky ? 'sticky' : 'relative'}
      pt={{ base: 400, tablet: 200 }}
      top={rest.top}
      width="100%"
      zIndex={zIndices.sticky}
      {...rest}
    >
      <Selector
        alignItems="center"
        defaultValue={undefined}
        height="100%"
        onChange={onStyleChange}
        size="small"
        value={selectedStyle || 'All Styles'}
        variant="glass"
      >
        <SelectorItem value="All Styles">{t('allStyles')}</SelectorItem>
        {styles?.map(styleItem => (
          <SelectorItem key={styleItem} value={styleItem}>
            {styleItem}
          </SelectorItem>
        ))}
      </Selector>
    </MotionHiddenScrollBox>
  );
};

export const BeforeAfterSwitch = ({
  setIsBeforeSelected,
  isBeforeSelected,
  trackingData,
  beforeText = 'before',
  afterText = 'after',
}: {
  setIsBeforeSelected: (isBeforeSelected: boolean) => void;
  isBeforeSelected: boolean;
  trackingData?: Partial<ButtonPressed>;
  beforeText?: string;
  afterText?: string;
}) => {
  const t = useProTranslate();
  const before = t(beforeText);
  const after = t(afterText);

  const onBeforeAfterSwitch = () => {
    buttonPressed({
      ...trackingData,
      button_text: isBeforeSelected ? after : before,
    });
    setIsBeforeSelected(!isBeforeSelected);
  };

  return (
    <Switch
      handleClick={onBeforeAfterSwitch}
      isOn={!isBeforeSelected}
      offText={before}
      onText={after}
    />
  );
};

const ImageGrid = ({
  onTryItCardClick,
  headerOffsetControls,
  styleSelectorIsSticky,
  trackingData,
  spaceTypes,
  styles,
  trackImageLoaded,
}: {
  onTryItCardClick: () => void;
  headerOffsetControls?: AnimationControls;
  styleSelectorIsSticky?: boolean;
  trackingData?: Partial<ButtonPressed>;
  spaceTypes?: LandingPageSpaceType[];
  styles?: LandingPageStyle[];
  trackImageLoaded?: (sampleImages: SampleImageMeta[]) => void;
}) => {
  const [style, setStyle] = useState<LandingPageStyle | undefined>(undefined);
  const [isBeforeSelected, setIsBeforeSelected] = useState(false);
  const random = useRandom();
  const [randomNumber] = useState(() => random());
  const sampleImages = getSampleImages(
    isBeforeSelected ? 'Original' : style || styles || 'Coastal',
    randomNumber,
    spaceTypes,
  );

  useEffect(() => {
    if (trackImageLoaded) {
      trackImageLoaded(sampleImages);
    }
  }, [sampleImages, trackImageLoaded]);

  return (
    <Flex
      flexDirection="column"
      paddingBottom={{ base: 1100, desktop: 1200 }}
      w={1}
    >
      <Flex
        flexDirection="column"
        mt={{ base: '300px', tablet: 300, desktop: 0 }}
        w={1}
      >
        <StyleSelector
          background="neutral.0"
          headerOffsetControls={headerOffsetControls}
          isSticky={styleSelectorIsSticky}
          selectedStyle={style}
          setIsBeforeSelected={setIsBeforeSelected}
          setStyle={setStyle}
          styles={styles}
          trackingData={trackingData}
        />
        <Box
          mb={{ base: '-150px', desktop: '0' }}
          paddingX={{ base: 500, tablet: 800 }}
          width="100%"
        >
          <Grid
            flexDirection={{ base: 'column', tablet: 'row' }}
            gridColumnGap={{ base: 200, tablet: 500 }}
            gridRowGap={{ base: 500, tablet: 600 }}
            gridTemplateColumns={{
              base: '1fr 1fr',
              tablet: '1fr 1fr',
              desktop: '1.25fr 1fr 1fr 1fr',
            }}
            gridTemplateRows={{
              base: '1fr .5fr 1fr',
              tablet: '1fr 1fr',
              desktop: '1fr 1fr',
            }}
            width="100%"
          >
            <Flex
              gridColumn={{
                base: '1 / span 2',
                tablet: '1',
                desktop: '1 / span 2',
              }}
              gridRow={{ base: '1', tablet: '1', desktop: '1 / span 2' }}
              height="100%"
              position="relative"
              width="100%"
            >
              {/* slot 1 */}
              <SampleImage
                bottomTextProps={smallerTextProps}
                imgUrl={sampleImages[0].imgUrl}
                isBeforeSelected={isBeforeSelected}
                ratio={1}
                spaceType={sampleImages[0].spaceType}
                styleName={sampleImages[0].style}
              />
              <Box
                bottom={1000}
                display={{ base: 'none', desktop: 'flex' }}
                height="50px"
                justifyContent="center"
                left="0"
                position="absolute"
                right="0"
              >
                <BeforeAfterSwitch
                  isBeforeSelected={isBeforeSelected}
                  setIsBeforeSelected={setIsBeforeSelected}
                  trackingData={{
                    ...trackingData,
                    track_id: 'before-after-toggle',
                    button_location: 'Style grid',
                  }}
                />
              </Box>
            </Flex>
            <Box
              gridColumn={{ base: '1', tablet: '2', desktop: '3' }}
              gridRow={{ base: '2', tablet: '1', desktop: '1' }}
            >
              {/* slot 2 */}
              <SampleImage
                bottomTextProps={smallerTextProps}
                imgUrl={sampleImages[1].imgUrl}
                isBeforeSelected={isBeforeSelected}
                ratio={1}
                spaceType={sampleImages[1].spaceType}
                styleName={sampleImages[1].style}
                width="100%"
              />
            </Box>
            <Box
              gridColumn={{ base: '2', tablet: '1', desktop: '4' }}
              gridRow={{ base: '2', tablet: '2', desktop: '1' }}
            >
              {/* slot 4 */}
              <SampleImage
                bottomTextProps={smallerTextProps}
                imgUrl={sampleImages[3].imgUrl}
                isBeforeSelected={isBeforeSelected}
                ratio={1}
                spaceType={sampleImages[3].spaceType}
                styleName={sampleImages[3].style}
                width="100%"
              />
            </Box>
            <Box
              gridColumn={{ base: '1 / span 2', tablet: '2', desktop: '3' }}
              gridRow={{ base: '3', tablet: '2', desktop: '2' }}
              paddingBottom={{ base: 400, tablet: 0 }}
              width="100%"
            >
              {/* slot 3 */}
              <SampleImage
                bottomTextProps={smallerTextProps}
                imgUrl={sampleImages[2].imgUrl}
                isBeforeSelected={isBeforeSelected}
                ratio={1}
                spaceType={sampleImages[2].spaceType}
                styleName={sampleImages[2].style}
              />
            </Box>
            <Box
              display={{ base: 'none', desktop: 'flex' }}
              gridColumn={{
                base: '1',
                tablet: '1 / span 2',
                desktop: '4',
              }}
              gridRow={{ base: '5', tablet: '3', desktop: '2' }}
              pb="60px"
              width="100%"
            >
              <TryItCard
                ctaTrackingData={{
                  ...trackingData,
                  track_id: 'sample-style-grid',
                }}
                onTryItCardClick={onTryItCardClick}
              />
            </Box>
          </Grid>
        </Box>
        <Box
          bottom={{ tablet: 700, mobile: 1100 }}
          display={{ base: 'flex', desktop: 'none' }}
          height="50px"
          justifyContent="center"
          margin="0 auto"
          position="sticky"
        >
          <BeforeAfterSwitch
            isBeforeSelected={isBeforeSelected}
            setIsBeforeSelected={setIsBeforeSelected}
            trackingData={{
              ...trackingData,
              track_id: 'before-after-toggle',
              button_location: 'Style grid',
            }}
          />
        </Box>
      </Flex>
      <Box
        display={{ base: 'block', desktop: 'none' }}
        mt={{ base: '100px', desktop: '0' }}
        pt={400}
        px={{ base: 500, tablet: 800 }}
        width="100%"
      >
        <TryItCard
          ctaTrackingData={{
            ...trackingData,
            track_id: 'sample-style-grid',
          }}
          onTryItCardClick={onTryItCardClick}
        />
      </Box>
    </Flex>
  );
};

export { ImageGrid };
